import { useState, useEffect } from "react";
import { Save, Globe, Palette, Phone, Mail } from "lucide-react";
import { siteSettingsService } from "@/services/firebase";
import { CloudinaryImageField } from "@/components/admin/CloudinaryImageField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import toast from "react-hot-toast";
import type { SiteSettings } from "@/types";

export function SettingsAdminPage() {
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    siteSettingsService.get().then((data) => {
      setSettings(data);
      setLoading(false);
    });
  }, []);

  const handleSave = async () => {
    if (!settings?.id) return;
    setSaving(true);
    try {
      await siteSettingsService.update(settings.id, settings);
      toast.success("Paramètres sauvegardés");
    } catch {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="animate-pulse">Chargement...</div>;
  if (!settings) return <div>Aucun paramètre trouvé</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-dark">Paramètres du Site</h1>
          <p className="text-gray-500 text-sm">Configuration générale de la plateforme</p>
        </div>
        <Button onClick={handleSave} isLoading={saving}>
          <Save className="w-4 h-4 mr-2" />
          Sauvegarder
        </Button>
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Globe className="w-5 h-5 text-primary" />
              Informations générales
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Input
                label="Nom du site"
                value={settings.siteName}
                onChange={(e) => setSettings({ ...settings, siteName: e.target.value })}
              />
              <Input
                label="Tagline"
                value={settings.tagline}
                onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
              />
            </div>
            <div className="grid lg:grid-cols-2 gap-6">
              <CloudinaryImageField
                label="Logo"
                value={settings.logo}
                folder="branding"
                onChange={(logo) => setSettings({ ...settings, logo })}
                hint="Utilisé dans la navigation publique et l'admin."
              />
              <CloudinaryImageField
                label="Favicon"
                value={settings.favicon}
                folder="branding"
                onChange={(favicon) => setSettings({ ...settings, favicon })}
                hint="Stocké dans les paramètres du site pour usage futur côté vitrine."
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Palette className="w-5 h-5 text-primary" />
              Couleurs
            </CardTitle>
          </CardHeader>
          <CardContent className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">Primaire</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={settings.primaryColor}
                  onChange={(e) => setSettings({ ...settings, primaryColor: e.target.value })}
                  className="w-10 h-10 rounded-lg cursor-pointer"
                />
                <Input value={settings.primaryColor} onChange={(e) => setSettings({ ...settings, primaryColor: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Secondaire</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={settings.secondaryColor}
                  onChange={(e) => setSettings({ ...settings, secondaryColor: e.target.value })}
                  className="w-10 h-10 rounded-lg cursor-pointer"
                />
                <Input value={settings.secondaryColor} onChange={(e) => setSettings({ ...settings, secondaryColor: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Sombre</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={settings.darkColor}
                  onChange={(e) => setSettings({ ...settings, darkColor: e.target.value })}
                  className="w-10 h-10 rounded-lg cursor-pointer"
                />
                <Input value={settings.darkColor} onChange={(e) => setSettings({ ...settings, darkColor: e.target.value })} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Phone className="w-5 h-5 text-primary" />
              Contact
            </CardTitle>
          </CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-4">
            <Input
              label="Téléphone"
              value={settings.contactPhone}
              onChange={(e) => setSettings({ ...settings, contactPhone: e.target.value })}
            />
            <Input
              label="WhatsApp"
              value={settings.contactWhatsApp}
              onChange={(e) => setSettings({ ...settings, contactWhatsApp: e.target.value })}
            />
            <Input
              label="Email"
              value={settings.contactEmail}
              onChange={(e) => setSettings({ ...settings, contactEmail: e.target.value })}
            />
            <Input
              label="Adresse"
              value={settings.contactAddress}
              onChange={(e) => setSettings({ ...settings, contactAddress: e.target.value })}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              SEO
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              label="Meta Title"
              value={settings.metaTitle}
              onChange={(e) => setSettings({ ...settings, metaTitle: e.target.value })}
            />
            <Textarea
              label="Meta Description"
              value={settings.metaDescription}
              onChange={(e) => setSettings({ ...settings, metaDescription: e.target.value })}
              rows={3}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
